import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Clock, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { getGuideBySlug, guides } from "@/data/guides";

export default function GuidePost() {
  const { slug } = useParams();
  const post = getGuideBySlug(slug);
  if (!post) return <Navigate to="/guide-ia" replace />;

  const others = guides.filter((g) => g.slug !== slug).slice(0, 3);
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "datePublished": post.publishedAt,
    "author": { "@type": "Organization", "name": "YAFORMIS" },
    "publisher": {
      "@type": "Organization",
      "name": "YAFORMIS",
      "logo": { "@type": "ImageObject", "url": "https://www.gme-assistance.fr/logo-yaformis.png" },
    },
  };

  return (
    <>
      <SEO
        title={post.seoTitle.replace(" | YAFORMIS", "").replace(" | YAFORMIS", "")}
        description={post.metaDescription}
        path={`/guide-ia/${post.slug}`}
        type="article"
        schema={articleSchema}
      />

      <article>
        <section className="bg-brand-page py-10 md:pt-14 pb-10 border-b border-brand-line" data-testid="guidepost-hero">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <Link to="/guide-ia" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-ink transition-colors" data-testid="guidepost-back">
              <ArrowLeft className="w-4 h-4" /> Tous les guides
            </Link>
            <div className="overline mt-6">{post.category}</div>
            <h1 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl mt-3 leading-[1.1] text-brand-ink hyphens-none">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {post.readingTime}
              </span>
              <span>·</span>
              <span>
                {new Date(post.publishedAt).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </section>

        <section className="bg-brand-page py-10 md:py-14" data-testid="guidepost-content">
          <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-5 font-body text-[17px] md:text-lg leading-[1.75] text-slate-700">
            {post.sections.map((s, i) => {
              if (s.type === "h2")
                return (
                  <h2 key={i} className="font-heading font-black text-2xl md:text-3xl tracking-tight text-brand-ink mt-10 mb-1 leading-tight">
                    {s.text}
                  </h2>
                );
              if (s.type === "h3")
                return (
                  <h3 key={i} className="font-heading font-bold text-xl tracking-tight text-brand-ink mt-7 mb-1">
                    {s.text}
                  </h3>
                );
              if (s.type === "quote")
                return (
                  <blockquote
                    key={i}
                    className="border-l-2 border-brand-blue pl-5 my-8 text-brand-ink font-heading font-semibold text-xl md:text-2xl leading-snug italic"
                  >
                    {s.text}
                  </blockquote>
                );
              if (s.type === "ul")
                return (
                  <ul key={i} className="space-y-2.5 pl-0 list-none">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex gap-3 items-start">
                        <span className="text-brand-blue font-bold mt-1.5 shrink-0">→</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              return <p key={i}>{s.text}</p>;
            })}
          </div>
        </section>

        <section className="bg-brand-surface py-12 md:py-16" data-testid="guidepost-cta">
          <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
            <div className="overline">Passer à la pratique</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl md:text-4xl mt-3 leading-tight text-brand-ink">
              Vous souhaitez maîtriser l'IA en entreprise ?
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              Découvrez nos quatre formations professionnelles, éligibles CPF et OPCO.
            </p>
            <Link to="/formations" className="mt-6 btn-cta" data-testid="guidepost-cta-button">
              Voir les formations <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        <section className="bg-brand-page py-12 md:py-16 border-t border-brand-line" data-testid="guidepost-related">
          <div className="max-w-5xl mx-auto px-5 md:px-8">
            <div className="overline">À lire aussi</div>
            <h2 className="font-heading font-black tracking-tighter text-2xl md:text-3xl mt-3 leading-tight text-brand-ink">
              Autres guides
            </h2>
            <ul className="mt-7 divide-y divide-brand-line border-y border-brand-line bg-white">
              {others.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/guide-ia/${g.slug}`}
                    data-testid={`guidepost-related-${g.slug}`}
                    className="group flex items-center justify-between gap-6 py-5 hover:text-brand-blue transition-colors"
                  >
                    <span className="flex-1">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-blue">
                        {g.category}
                      </span>
                      <span className="block font-heading font-bold text-lg md:text-xl text-brand-ink group-hover:text-brand-blue mt-1 leading-snug">
                        {g.title}
                      </span>
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-brand-blue group-hover:rotate-45 transition-all shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </article>
    </>
  );
}
